import { parseSlideLayoutJson } from '../types/programContentSlideCanvas';
import { supabase } from '../supabaseClient';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  loadProgramSlideRegisteredPages,
  saveProgramSlideRegisteredPages,
  type ProgramSlideRegisteredPage,
} from '../utils/programSlideRegisteredPages';
import {
  normalizeProgramSlideRegistryName,
  PROGRAM_SLIDE_REGISTERED_PAGE_MAX_COUNT,
  programSlideRegistryFallbackName,
} from '../utils/programSlideRegistryLimits';
import {
  clearRegistryScopeRows,
  createRegistryItemId,
  dedupeRegistryItemIds,
  formatRegistryScopeSaveError,
  insertRegistryScopeRowsWithRetry,
  isRegistryItemId,
} from '../utils/programSlideRegisteredScopeSave';

const REGISTRY_SCOPE_TABLE = 'workspace_slide_registered_pages' as const;

const SELECT_COLUMNS = 'id, workspace_id, name, layout_json, order_index, created_at, updated_at';

interface WorkspaceSlideRegisteredPageRow {
  id: string;
  workspace_id: string;
  name: string;
  layout_json: unknown;
  order_index: number;
  created_at: string;
  updated_at: string;
}

function normalizeName(raw: unknown, fallback: string): string {
  return normalizeProgramSlideRegistryName(raw, fallback);
}

function rowToPage(row: WorkspaceSlideRegisteredPageRow, idx: number): ProgramSlideRegisteredPage {
  return {
    id: row.id,
    name: normalizeName(row.name, programSlideRegistryFallbackName(idx)),
    layout: parseSlideLayoutJson(row.layout_json),
    order:
      Number.isFinite(Number(row.order_index)) && Number(row.order_index) >= 0
        ? Number(row.order_index)
        : idx,
    updatedAt: row.updated_at ? new Date(row.updated_at).getTime() : Date.now(),
  };
}

function sortPages(pages: ProgramSlideRegisteredPage[]): ProgramSlideRegisteredPage[] {
  return [...pages].sort((a, b) => a.order - b.order);
}

/** id:updatedAt(ms) 조합의 head 시그니처. layout_json 본문 없이 캐시 신선도만 검증할 때 사용. */
function buildRegisteredPagesHeadVersion(pages: Array<{ id: string; updatedAt: number }>): string {
  const parts = pages.map((p) => `${p.id}:${p.updatedAt}`).sort();
  return `${parts.length}|${parts.join(',')}`;
}

async function fetchProgramSlideRegisteredPagesHeadVersion(workspaceId: string): Promise<string> {
  const { data, error } = await supabase
    .from('workspace_slide_registered_pages')
    .select('id, updated_at')
    .eq('workspace_id', workspaceId);
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r) => {
    const row = r as Record<string, unknown>;
    return {
      id: String(row.id),
      updatedAt: row.updated_at ? new Date(String(row.updated_at)).getTime() : 0,
    };
  });
  return buildRegisteredPagesHeadVersion(rows);
}

async function fetchProgramSlideRegisteredPagesFromDb(
  workspaceId: string
): Promise<ProgramSlideRegisteredPage[]> {
  const { data, error } = await supabase
    .from('workspace_slide_registered_pages')
    .select(SELECT_COLUMNS)
    .eq('workspace_id', workspaceId)
    .order('order_index', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  const rows = ((data ?? []) as unknown) as WorkspaceSlideRegisteredPageRow[];
  return sortPages(rows.map((row, idx) => rowToPage(row, idx)));
}

/**
 * `forceRefresh` 없으면 LS 캐시가 있을 때 `id/updated_at` head만 먼저 비교하고, 동일하면
 * layout_json 본문 재조회 없이 캐시를 재사용한다. head 조회 실패(오프라인 등) 시에는 캐시를 그대로 반환한다.
 */
export async function getProgramSlideRegisteredPages(
  workspaceId: string,
  options?: { forceRefresh?: boolean }
): Promise<ProgramSlideRegisteredPage[]> {
  const sid = workspaceId.trim();
  if (!sid) return [];

  const persistFromDb = (rows: ProgramSlideRegisteredPage[]) => {
    // get 경로의 LS 반영은 이벤트 없이 — 리스너가 다시 forceRefresh 하는 루프 방지
    saveProgramSlideRegisteredPages(sid, rows, { emitEvent: false });
  };

  if (!options?.forceRefresh) {
    const cached = loadProgramSlideRegisteredPages(sid);
    if (cached.length > 0) {
      const cachedVersion = buildRegisteredPagesHeadVersion(cached);
      let headVersion: string | null = null;
      try {
        headVersion = await fetchProgramSlideRegisteredPagesHeadVersion(sid);
      } catch {
        headVersion = null;
      }
      if (headVersion === null) {
        return cached;
      }
      if (headVersion === cachedVersion) {
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('슬라이드페이지등록·LS(head일치)');
        }
        return cached;
      }
      // head 불일치 → 아래에서 본문 재조회
    }
  }

  const fromDb = await fetchProgramSlideRegisteredPagesFromDb(sid);
  if (fromDb.length > 0) {
    persistFromDb(fromDb);
    return fromDb;
  }

  const legacyLocal = loadProgramSlideRegisteredPages(sid);
  if (legacyLocal.length > 0) {
    return legacyLocal;
  }

  persistFromDb([]);
  return [];
}

function buildRegisteredPagePayload(
  sid: string,
  normalized: Array<{
    id: string;
    name: string;
    layout: ProgramSlideRegisteredPage['layout'];
    order: number;
  }>,
  now: string
) {
  return normalized.map((page) => ({
    id: page.id,
    workspace_id: sid,
    name: page.name,
    layout_json: page.layout,
    order_index: page.order,
    created_at: now,
    updated_at: now,
  }));
}

type NormalizedRegisteredPage = {
  id: string;
  name: string;
  layout: ProgramSlideRegisteredPage['layout'];
  order: number;
};

async function insertRegisteredPages(
  sid: string,
  normalized: NormalizedRegisteredPage[],
  now: string
): Promise<WorkspaceSlideRegisteredPageRow[]> {
  const data = await insertRegistryScopeRowsWithRetry(
    '페이지',
    REGISTRY_SCOPE_TABLE,
    sid,
    normalized,
    async (rows) => {
      const payload = buildRegisteredPagePayload(sid, rows, now);
      return supabase
        .from(REGISTRY_SCOPE_TABLE)
        .insert(payload)
        .select(SELECT_COLUMNS)
        .order('order_index', { ascending: true });
    }
  );
  return (data ?? []) as unknown as WorkspaceSlideRegisteredPageRow[];
}

export async function replaceProgramSlideRegisteredPages(
  workspaceId: string,
  pages: ProgramSlideRegisteredPage[]
): Promise<ProgramSlideRegisteredPage[]> {
  const sid = workspaceId.trim();
  if (!sid) return [];

  const now = new Date().toISOString();
  const sliced = pages.slice(0, PROGRAM_SLIDE_REGISTERED_PAGE_MAX_COUNT);
  const dedupedIds = dedupeRegistryItemIds(
    sliced.map((page) => String(page.id ?? '').trim())
  );
  const normalized = sliced
    .map((page, idx) => ({
      id: dedupedIds[idx] ?? createRegistryItemId(),
      name: normalizeName(page.name, programSlideRegistryFallbackName(idx)),
      layout: page.layout,
      order: idx,
      updatedAt: Number.isFinite(page.updatedAt) ? page.updatedAt : Date.now(),
    }))
    .filter((page) => isRegistryItemId(page.id));

  await clearRegistryScopeRows(REGISTRY_SCOPE_TABLE, sid, '페이지');

  if (!normalized.length) {
    saveProgramSlideRegisteredPages(sid, []);
    return [];
  }

  const rows = await insertRegisteredPages(sid, normalized, now);
  const saved = sortPages(rows.map((row, idx) => rowToPage(row, idx)));
  saveProgramSlideRegisteredPages(sid, saved);
  return saved;
}
